import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownRight, ArrowUpRight, EyeOff, Factory, Lock, Unplug } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageContainer } from '../../../common/components/PageContainer';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { BRAND_PERIODS, BrandMe, BrandPeriod, ShopLabel, SuppressionReason, isBrandAccount, useBrandApi } from '../../../common/hooks/use-brand-api.hook';
import { EmptyState, PillTabs } from '../../../components/design-system/admin';
import { InfoTip } from '../../../components/design-system/analytics';
import { VizHeader } from '../../vendor/analytics/viz/components/VizHeader';
import { countryName, shopName } from '../brand-format';
import '../brand-i18n';

/** The frame every Brand page shares: breadcrumbs, a reading width, the title row and its controls. */
export function BrandPage({
    title,
    description,
    crumb,
    actions,
    children,
    testId,
}: {
    title: ReactNode;
    description?: ReactNode;
    crumb: string;
    actions?: ReactNode;
    children: ReactNode;
    testId?: string;
}) {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('BRAND_PORTAL.WORKSPACE') }, { label: crumb }]}>
            <BrandGate>
                <div className="mx-auto flex max-w-[1200px] flex-col gap-6" data-testid={testId}>
                    <VizHeader title={title} description={description} actions={actions} />
                    {children}
                </div>
            </BrandGate>
        </PageContainer>
    );
}

/** 7, 30 or 90 days — the windows the API compares against the one before. */
export function PeriodPills({ value, onChange }: { value: BrandPeriod; onChange: (p: BrandPeriod) => void }) {
    const { t } = useTranslation();
    return (
        <PillTabs
            value={value}
            onChange={onChange}
            ariaLabel={t('BRAND_PORTAL.COMMON.PERIOD')}
            options={BRAND_PERIODS.map((p) => ({ value: p, label: t(`BRAND_PORTAL.PERIOD.${p.toUpperCase()}`) }))}
        />
    );
}

let meCache: { at: number; value: BrandMe } | null = null;

/** Who the brand is, once per minute at most — the gate and the headers both ask. */
export function useBrandMe() {
    const { user } = useAuthContext();
    const api = useBrandApi();
    const brand = isBrandAccount(user);
    const [me, setMe] = useState<BrandMe | null>(meCache && Date.now() - meCache.at < 60_000 ? meCache.value : null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        if (!brand || me) return;
        let live = true;
        api.me()
            .then((value) => {
                meCache = { at: Date.now(), value };
                if (live) setMe(value);
            })
            .catch(() => live && setFailed(true));
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brand]);
    return { isBrand: brand, me, failed };
}

/**
 * Only a manufacturer's account sees the Brand pages, and only once Ardealy has
 * connected it to its brand. Checked before any page asks the API anything:
 * the API refuses a retailer with a 403, and the app signs a user out on one.
 */
export function BrandGate({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const { isBrand, me, failed } = useBrandMe();
    if (!isBrand) {
        return (
            <Card data-testid="brand-not-brand">
                <CardContent>
                    <EmptyState icon={<Factory />} title={t('BRAND_PORTAL.GATE.RETAILER_TITLE')} description={t('BRAND_PORTAL.GATE.RETAILER_TEXT')} />
                </CardContent>
            </Card>
        );
    }
    if (failed) return <LoadFailed />;
    if (!me) return null;
    if (!me.connected) {
        return (
            <Card data-testid="brand-not-connected">
                <CardContent>
                    <EmptyState icon={<Unplug />} title={t('BRAND_PORTAL.GATE.NOT_CONNECTED_TITLE')} description={t('BRAND_PORTAL.GATE.NOT_CONNECTED_TEXT')} />
                </CardContent>
            </Card>
        );
    }
    return <>{children}</>;
}

export function LoadFailed({ onRetry }: { onRetry?: () => void }) {
    const { t } = useTranslation();
    return (
        <Card data-testid="brand-load-failed">
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">{t('BRAND_PORTAL.COMMON.LOAD_FAILED')}</p>
                {onRetry && (
                    <Button variant="outline" size="sm" onClick={onRetry}>
                        {t('BRAND_PORTAL.COMMON.RETRY')}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * Why there are no numbers to show — said plainly, with the rule behind it,
 * and without saying which retailer it is about.
 */
export function SuppressedNotice({ reason, className }: { reason: SuppressionReason; className?: string }) {
    const { t } = useTranslation();
    const key = reason.toUpperCase();
    return (
        <Card className={className} data-testid={`brand-suppressed-${reason}`}>
            <CardContent>
                <EmptyState
                    icon={reason === 'no_retailers' || reason === 'no_demand' ? <EyeOff /> : <Lock />}
                    title={t(`BRAND_PORTAL.SUPPRESSED.${key}_TITLE`)}
                    description={t(`BRAND_PORTAL.SUPPRESSED.${key}_TEXT`)}
                />
            </CardContent>
        </Card>
    );
}

/** One line under a breakdown: what is grouped as Other, and why. */
export function PrivacyFootnote({ className }: { className?: string }) {
    const { t } = useTranslation();
    return (
        <p className={cn('flex items-center gap-1.5 text-xs text-muted-foreground', className)}>
            <Lock className="size-3 shrink-0" aria-hidden />
            <span>{t('BRAND_PORTAL.PRIVACY.FOOTNOTE')}</span>
            <InfoTip label={t('BRAND_PORTAL.PRIVACY.WHY')}>{t('BRAND_PORTAL.PRIVACY.EXPLAINER')}</InfoTip>
        </p>
    );
}

/** A shop as the brand may see it: named when listed, otherwise "A retailer in France" with why. */
export function ShopNameText({ label, className }: { label: ShopLabel; className?: string }) {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    if (label.kind === 'listed') {
        return (
            <span className={cn('min-w-0', className)}>
                <span className="font-medium">{label.name}</span>
                {(label.city || label.country) && (
                    <span className="text-muted-foreground"> · {[label.city, countryName(label.country, lang)].filter(Boolean).join(', ')}</span>
                )}
            </span>
        );
    }
    return (
        <span className={cn('inline-flex min-w-0 items-center gap-1 text-muted-foreground', className)}>
            <span className="truncate">{shopName(t, label, lang)}</span>
            <InfoTip label={t('BRAND_PORTAL.SHOP.WHY_HIDDEN')}>{t('BRAND_PORTAL.SHOP.WHY_HIDDEN_TEXT')}</InfoTip>
        </span>
    );
}

/** A percentage change, coloured only by direction; nothing when there is nothing to compare with. */
export function Change({ value, className, suffix = '%' }: { value: number | null | undefined; className?: string; suffix?: string }) {
    if (value == null || Math.round(value) === 0) return null;
    const up = value > 0;
    return (
        <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium tabular-nums', up ? 'text-success' : 'text-destructive', className)}>
            {up ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
            {Math.abs(Math.round(value))}
            {suffix}
        </span>
    );
}

/** A withheld value in a table cell: a dash with the reason on hover. */
export function Withheld() {
    const { t } = useTranslation();
    return (
        <span className="inline-flex items-center gap-1 text-muted-foreground" title={t('BRAND_PORTAL.PRIVACY.WITHHELD')}>
            —
        </span>
    );
}
